import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { studyDashboardQueryKeys } from "./studyDashboardQueryKeys";
import {
  fetchStudyDashboardDailyGoal,
  fetchDashboardExploreStore,
  fetchDashboardHandbookList,
  fetchDashboardStoreBanner,
  fetchDashboardHomeSliderBanners,
  fetchStudyDashboardLeaderboard,
  fetchStudyDashboardProgress,
  fetchStudyDashboardResumeVideo,
  fetchStudyDashboardResumeTests,
  fetchStudyDashboardScheduleContent,
  fetchStudyDashboardScheduleWeek,
  fetchStudyDashboardStreak,
  fetchStudyDashboardTopWeakTopics,
  fetchStudyDashboardTestWiseAnalysis,
  fetchStudyDashboardSubjectPerformance,
} from "./studyDashboardApi";

const DASHBOARD_STALE_TIME = 5 * 60 * 1000;

const dashboardQueryOptions = {
  staleTime: DASHBOARD_STALE_TIME,
  refetchOnWindowFocus: false,
  retry: 1,
};

export const useStudyDashboardAuth = () => {
  const token = useSelector((state) => state.auth.authToken);
  const tokenType = useSelector((state) => state.auth.tokenType);
  const courseId = useSelector(
    (state) => state.userPreference?.courseDetails?.[0]?.course_id
  );
  const batchId = useSelector((state) => state.userPreference?.batchDetails?.batch_id);
  const classId = useSelector((state) => state.userPreference?.classDetails?.class_id);
  const targetId = useSelector(
    (state) => state.userPreference?.targetDetails?.[0]?.target_id
  );

  const authorization =
    token && tokenType ? `${tokenType} ${token}` : "";

  return {
    authorization,
    courseId,
    batchId,
    classId,
    targetId,
    isAuthenticated: Boolean(authorization),
    hasSchedulePrefs: Boolean(authorization && courseId && batchId),
  };
};

export const useStudyDashboardProgressQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.progress(authorization),
    queryFn: () => fetchStudyDashboardProgress(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useStudyDashboardStreakQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.streak(authorization),
    queryFn: () => fetchStudyDashboardStreak(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useStudyDashboardLeaderboardQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.leaderboard(authorization),
    queryFn: () => fetchStudyDashboardLeaderboard(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useStudyDashboardResumeVideoQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.resumeVideo(authorization),
    queryFn: () => fetchStudyDashboardResumeVideo(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useStudyDashboardResumeTestsQuery = () => {
  const { authorization, isAuthenticated, courseId, batchId, classId, targetId } =
    useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.resumeTests(
      authorization,
      courseId,
      batchId,
      classId,
      targetId
    ),
    queryFn: () =>
      fetchStudyDashboardResumeTests(authorization, {
        courseId,
        batchId,
        classId,
        targetId,
      }),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useDashboardExploreStoreQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.exploreStore(authorization),
    queryFn: () => fetchDashboardExploreStore(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useDashboardHandbookListQuery = (productId = "", enabled = true) => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.handbookList(authorization, productId),
    queryFn: () => fetchDashboardHandbookList(authorization, productId),
    enabled: isAuthenticated && enabled,
    ...dashboardQueryOptions,
  });
};

export const useDashboardStoreBannerQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.storeBanner(authorization),
    queryFn: () => fetchDashboardStoreBanner(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useDashboardHomeSliderBannersQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.homeSliderBanners(authorization),
    queryFn: () => fetchDashboardHomeSliderBanners(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useStudyDashboardDailyGoalQuery = (enabled) => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.dailyGoal(authorization),
    queryFn: () => fetchStudyDashboardDailyGoal(authorization),
    enabled: isAuthenticated && enabled,
    ...dashboardQueryOptions,
  });
};

export const useStudyDashboardTopWeakTopicsQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.topWeakTopics(authorization),
    queryFn: () => fetchStudyDashboardTopWeakTopics(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
    staleTime: 10 * 60 * 1000,
  });
};

export const useStudyDashboardTestWiseAnalysisQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.testWiseAnalysis(authorization),
    queryFn: () => fetchStudyDashboardTestWiseAnalysis(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnReconnect: true,
  });
};

export const useStudyDashboardSubjectPerformanceQuery = () => {
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.subjectPerformance(authorization),
    queryFn: () => fetchStudyDashboardSubjectPerformance(authorization),
    enabled: isAuthenticated,
    ...dashboardQueryOptions,
  });
};

export const useStudyDashboardScheduleWeekQuery = () => {
  const { authorization, courseId, batchId, hasSchedulePrefs } =
    useStudyDashboardAuth();

  return useQuery({
    queryKey: studyDashboardQueryKeys.scheduleWeek(authorization, courseId, batchId),
    queryFn: () =>
      fetchStudyDashboardScheduleWeek(authorization, courseId, batchId),
    enabled: hasSchedulePrefs,
    ...dashboardQueryOptions,
  });
};

export const useStudyDashboardScheduleContentQuery = ({
  dayName,
  rawDate,
  enabled = true,
}) => {
  const { authorization, courseId, batchId, hasSchedulePrefs } =
    useStudyDashboardAuth();

  const canFetch = Boolean(
    enabled && hasSchedulePrefs && dayName && rawDate
  );

  return useQuery({
    queryKey: studyDashboardQueryKeys.scheduleContent(
      authorization,
      courseId,
      batchId,
      dayName,
      rawDate
    ),
    queryFn: () =>
      fetchStudyDashboardScheduleContent(
        authorization,
        courseId,
        batchId,
        dayName,
        rawDate
      ),
    enabled: canFetch,
    ...dashboardQueryOptions,
  });
};

export const useInvalidateStudyDashboardProgress = () => {
  const queryClient = useQueryClient();
  const { authorization } = useStudyDashboardAuth();

  return () =>
    queryClient.invalidateQueries({
      queryKey: studyDashboardQueryKeys.progress(authorization),
    });
};

/** Force-refresh resume + recent tests (e.g. every dashboard visit / after resume exams). */
export const useRefreshStudyDashboardResumeAndRecentTests = () => {
  const queryClient = useQueryClient();
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return useCallback(() => {
    if (!isAuthenticated || !authorization) return Promise.resolve();

    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: studyDashboardQueryKeys.resumeVideo(authorization),
      }),
      queryClient.invalidateQueries({
        queryKey: studyDashboardQueryKeys.testWiseAnalysis(authorization),
      }),
    ]);
  }, [authorization, isAuthenticated, queryClient]);
};

export const useRefreshStudyDashboardAfterGoalSave = () => {
  const queryClient = useQueryClient();
  const { authorization } = useStudyDashboardAuth();

  return async (savedGoal) => {
    if (savedGoal != null && authorization) {
      queryClient.setQueryData(
        studyDashboardQueryKeys.dailyGoal(authorization),
        (current) => (current ? { ...current, saved_goal: savedGoal } : current)
      );
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: studyDashboardQueryKeys.progress(authorization),
      }),
      queryClient.fetchQuery({
        queryKey: studyDashboardQueryKeys.dailyGoal(authorization),
        queryFn: () => fetchStudyDashboardDailyGoal(authorization),
        staleTime: 0,
      }),
    ]);
  };
};

export const useFetchStudyDashboardDailyGoal = () => {
  const queryClient = useQueryClient();
  const { authorization, isAuthenticated } = useStudyDashboardAuth();

  return () => {
    if (!isAuthenticated) return Promise.resolve(null);
    return queryClient.fetchQuery({
      queryKey: studyDashboardQueryKeys.dailyGoal(authorization),
      queryFn: () => fetchStudyDashboardDailyGoal(authorization),
      staleTime: 0,
    });
  };
};
